// components/Admin/EmoticonManager/EmoticonTable.jsx
import React, { useState, useMemo } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, IconButton, Chip, TextField, Button, Box, Dialog,
    DialogTitle, DialogContent, DialogActions, Typography,
    Checkbox, FormControlLabel, Pagination, Stack, Select, MenuItem,
    useMediaQuery, useTheme
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import api from '../../../api/axios';

const getFullImageUrl = (url) =>
    url?.startsWith('http') ? url : `https://sandle.it.com${url}`;

const EmoticonTable = ({ emoticons, refresh }) => {
    const ITEMS_PER_PAGE = 5;
    const [page, setPage] = useState(1);
    const [editItem, setEditItem] = useState(null);
    const [newImage, setNewImage] = useState(null);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const filteredEmoticons = useMemo(() => (
        emoticons
            .filter((e) => {
                if (filter === 'active') return e.is_active;
                if (filter === 'inactive') return !e.is_active;
                return true;
            })
            .filter((e) =>
                !search.trim() || e.name?.toLowerCase().includes(search.toLowerCase()))
    ), [emoticons, search, filter]);

    const paginatedEmoticons = filteredEmoticons.slice(
        (page - 1) * ITEMS_PER_PAGE,
        page * ITEMS_PER_PAGE
    );

    const handleDelete = (id) => {
        if (window.confirm('정말 삭제하시겠습니까?')) {
            api.delete(`/api/management/emoticons/${id}/`)
                .then(() => refresh())
                .catch(() => alert('삭제 실패'));
        }
    };

    const handleToggleActive = (id, current) => {
        api.patch(`/api/management/emoticons/${id}/`, { is_active: !current })
            .then(() => refresh())
            .catch(() => alert('변경 실패'));
    };

    const handleEdit = (item) => {
        setEditItem({ ...item });
        setNewImage(null);
    };

    const handleEditSave = () => {
        const { id, name, price, stock, is_unlimited, emoji_text } = editItem;
        const formData = new FormData();
        formData.append('name', name);
        formData.append('price', price);
        formData.append('stock', stock);
        formData.append('is_unlimited', is_unlimited);
        formData.append('emoji_text', emoji_text ?? '');
        if (newImage) {
            formData.append('image', newImage);
        }

        api.patch(`/api/management/emoticons/${id}/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
            .then(() => {
                setEditItem(null);
                setNewImage(null);
                refresh();
            })
            .catch(() => alert('수정 실패'));
    };

    return (
        <>
            {/* 🔍 검색 + 필터 + 건수 */}
            <Stack
                direction={isMobile ? 'column' : 'row'}
                spacing={2}
                sx={{ mb: 2 }}
                flexWrap="wrap"
                alignItems="center"
            >
                <TextField
                    label="이모티콘명 검색"
                    size="small"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    fullWidth={isMobile}
                />
                <Select
                    size="small"
                    value={filter}
                    onChange={(e) => { setFilter(e.target.value); setPage(1); }}
                    fullWidth={isMobile}
                >
                    <MenuItem value="all">전체</MenuItem>
                    <MenuItem value="active">노출 중</MenuItem>
                    <MenuItem value="inactive">숨김</MenuItem>
                </Select>
                <Typography
                    variant="body2"
                    sx={{
                        ml: isMobile ? 0 : 'auto',
                        mt: isMobile ? 1 : 0,
                        textAlign: isMobile ? 'right' : 'left',
                    }}
                >
                    총 {filteredEmoticons.length}건
                </Typography>
            </Stack>

            <TableContainer
                component={Paper}
                sx={{
                    overflowX: 'auto',
                    borderRadius: 2,
                    '@media (max-width:600px)': {
                        transform: 'scale(0.95)',
                        transformOrigin: 'top left',
                        minWidth: 'max-content',
                    },
                }}
            >
                <Table size="medium">
                    <TableHead>
                        <TableRow>
                            <TableCell>미리보기</TableCell>
                            <TableCell>이름</TableCell>
                            <TableCell>가격</TableCell>
                            <TableCell>재고</TableCell>
                            <TableCell>상태</TableCell>
                            <TableCell align="right">관리</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedEmoticons.map((e) => (
                            <TableRow key={e.id}>
                                <TableCell align="center">
                                    {e.image ? (
                                        <img
                                            src={getFullImageUrl(e.image)}
                                            alt={e.name}
                                            width={36}
                                            height={36}
                                            style={{ objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <Typography fontSize={24}>{e.emoji_text || '—'}</Typography>
                                    )}
                                </TableCell>
                                <TableCell>{e.name}</TableCell>
                                <TableCell>{e.price} Leaf🌿</TableCell>
                                <TableCell>
                                    {e.is_unlimited ? '무제한' : `${e.stock ?? 0}개`}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={e.is_active ? '노출중' : '숨김'}
                                        color={e.is_active ? 'success' : 'default'}
                                        size="small"
                                        icon={e.is_active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                        onClick={() => handleToggleActive(e.id, e.is_active)}
                                        clickable
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => handleEdit(e)}><EditIcon /></IconButton>
                                    <IconButton onClick={() => handleDelete(e.id)}><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {filteredEmoticons.length > ITEMS_PER_PAGE && (
                <Box
                    display="flex"
                    justifyContent="center"
                    mt={2}
                    sx={{
                        '@media (max-width:600px)': {
                            transform: 'scale(0.9)',
                        },
                    }}
                >
                    <Pagination
                        count={Math.ceil(filteredEmoticons.length / ITEMS_PER_PAGE)}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        color="primary"
                        shape="rounded"
                    />
                </Box>
            )}

            {editItem && (
                <Dialog open onClose={() => setEditItem(null)} maxWidth="xs" fullWidth>
                    <DialogTitle>이모티콘 수정</DialogTitle>
                    <DialogContent>
                        <Box display="flex" flexDirection="column" gap={2} mt={1}>
                            <TextField
                                label="이름"
                                value={editItem.name ?? ''}
                                onChange={(e) => setEditItem({ ...editItem, name: e.target.value })}
                                fullWidth
                            />
                            <TextField
                                label="가격"
                                type="number"
                                value={editItem.price ?? ''}
                                onChange={(e) => setEditItem({ ...editItem, price: e.target.value })}
                                fullWidth
                            />
                            <TextField
                                label="수량"
                                type="number"
                                value={editItem.stock ?? ''}
                                onChange={(e) => setEditItem({ ...editItem, stock: e.target.value })}
                                fullWidth
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={editItem.is_unlimited ?? true}
                                        onChange={(e) => setEditItem({ ...editItem, is_unlimited: e.target.checked })}
                                    />
                                }
                                label="무제한으로 설정"
                            />
                            <TextField
                                label="텍스트 이모지 (😊 등)"
                                value={editItem.emoji_text ?? ''}
                                onChange={(e) => setEditItem({ ...editItem, emoji_text: e.target.value })}
                                inputProps={{ maxLength: 5 }}
                                fullWidth
                            />
                            <Button variant="outlined" component="label">
                                이미지 수정
                                <input type="file" hidden onChange={e => setNewImage(e.target.files[0])} />
                            </Button>
                            {newImage && (
                                <Typography variant="caption" color="text.secondary">
                                    새로운 이미지 선택됨: {newImage.name}
                                </Typography>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEditItem(null)}>취소</Button>
                        <Button variant="contained" onClick={handleEditSave}>저장</Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};

export default EmoticonTable;
